<template>
  <div class="restaurant-orders-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="restaurant-orders-container">
          <!-- Page Header -->
          <div class="page-header">
            <div class="header-content">
              <div class="header-left">
                <h1 class="page-title">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" class="title-icon">
                    <path d="M21,16.5C21,16.88 20.79,17.21 20.47,17.38L12.57,21.82C12.41,21.94 12.21,22 12,22C11.79,22 11.59,21.94 11.43,21.82L3.53,17.38C3.21,17.21 3,16.88 3,16.5V7.5C3,7.12 3.21,6.79 3.53,6.62L11.43,2.18C11.59,2.06 11.79,2 12,2C12.21,2 12.41,2.06 12.57,2.18L20.47,6.62C20.79,6.79 21,7.12 21,7.5V16.5M12,4.15L5,8.09V15.91L12,19.85L19,15.91V8.09L12,4.15Z"/>
                  </svg>
                  Orders
                </h1>
                <p class="page-subtitle">Manage customer orders</p>
              </div>
            </div>
          </div>

          <!-- Filters -->
          <div class="filters-section">
            <div class="filter-buttons">
              <button
                v-for="status in orderStatuses"
                :key="status.value"
                @click="orderFilter = status.value"
                :class="['filter-btn', { active: orderFilter === status.value }]"
              >
                <span class="filter-icon">{{ status.icon }}</span>
                {{ status.label }}
              </button>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="loading-state">
            <div class="loading-spinner"></div>
            <p>Loading orders...</p>
          </div>

          <!-- Empty State -->
          <div v-else-if="filteredOrders.length === 0" class="empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" class="empty-icon">
              <path d="M21,16.5C21,16.88 20.79,17.21 20.47,17.38L12.57,21.82C12.41,21.94 12.21,22 12,22C11.79,22 11.59,21.94 11.43,21.82L3.53,17.38C3.21,17.21 3,16.88 3,16.5V7.5C3,7.12 3.21,6.79 3.53,6.62L11.43,2.18C11.59,2.06 11.79,2 12,2C12.21,2 12.41,2.06 12.57,2.18L20.47,6.62C20.79,6.79 21,7.12 21,7.5V16.5M12,4.15L5,8.09V15.91L12,19.85L19,15.91V8.09L12,4.15Z"/>
            </svg>
            <h3>No Orders Yet</h3>
            <p>Orders will appear here once customers start ordering!</p>
          </div>

          <!-- Orders List -->
          <div v-else class="orders-list">
            <div
              v-for="order in filteredOrders"
              :key="order.id"
              class="order-card"
            >
              <div class="order-header">
                <div class="order-info">
                  <h4 class="order-number">{{ order.order_number }}</h4>
                  <span class="order-date">{{ formatDateTime(order.placed_at) }}</span>
                </div>
                <span :class="['order-status', getStatusClass(order.order_status)]">
                  {{ getStatusLabel(order.order_status) }}
                </span>
              </div>

              <div class="order-details">
                <div class="detail-row">
                  <span class="detail-label">Customer:</span>
                  <span class="detail-value">{{ order.customer_name }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Phone:</span>
                  <a :href="`tel:${order.customer_phone}`" class="detail-value phone-link">
                    {{ order.customer_phone }}
                  </a>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Address:</span>
                  <span class="detail-value">{{ order.delivery_address }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Payment:</span>
                  <span class="detail-value">{{ order.payment_method || 'Cash on Delivery' }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Total:</span>
                  <span class="detail-value total-amount">{{ order.total }} EGP</span>
                </div>
              </div>

              <div class="order-items">
                <h5 class="items-title">Order Items:</h5>
                <ul class="items-list">
                  <li v-for="(item, index) in parseItems(order.items)" :key="index" class="item-entry">
                    <span class="item-name">{{ item.name }}</span>
                    <span class="item-quantity">x{{ item.quantity }}</span>
                    <span class="item-price">{{ (item.price * item.quantity).toFixed(2) }} EGP</span>
                  </li>
                </ul>
              </div>

              <div class="order-actions">
                <select
                  v-model="order.order_status"
                  @change="updateOrderStatus(order)"
                  class="status-select"
                  :class="getStatusClass(order.order_status)"
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="preparing">Preparing</option>
                  <option value="ready">Ready</option>
                  <option value="out_for_delivery">Out for Delivery</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { authStore } from '@/store/auth'

export default {
  name: 'RestaurantOrdersView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      loading: false,
      orders: [],
      orderFilter: 'all',
      orderStatuses: [
        { value: 'all', label: 'All', icon: '📋' },
        { value: 'pending', label: 'Pending', icon: '⏳' },
        { value: 'confirmed', label: 'Confirmed', icon: '✅' },
        { value: 'preparing', label: 'Preparing', icon: '👨‍🍳' },
        { value: 'ready', label: 'Ready', icon: '✅' },
        { value: 'out_for_delivery', label: 'Delivering', icon: '🚚' },
        { value: 'delivered', label: 'Delivered', icon: '✅' },
        { value: 'cancelled', label: 'Cancelled', icon: '❌' }
      ]
    }
  },
  computed: {
    filteredOrders () {
      if (this.orderFilter === 'all') {
        return this.orders
      }
      return this.orders.filter(order => order.order_status === this.orderFilter)
    }
  },
  async mounted () {
    await this.checkRestaurantMode()
    this.loadOrders()
    // Refresh orders every 30 seconds
    this.refreshInterval = setInterval(() => {
      this.loadOrders()
    }, 30000)
  },
  beforeUnmount () {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
  },
  methods: {
    async checkRestaurantMode () {
      try {
        const token = localStorage.getItem('auth_token')
        const response = await fetch('https://api.quali-d.com/restaurant/mode/check', {
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await response.json()
        if (data.success) {
          authStore.setRestaurantMode(data.restaurant_mode || false)
        }
      } catch (error) {
        console.error('Error checking restaurant mode:', error)
      }
    },

    async loadOrders () {
      try {
        this.loading = true
        const token = localStorage.getItem('auth_token')
        const response = await fetch('https://api.quali-d.com/restaurant-mode/orders', {
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await response.json()
        if (data.success) {
          this.orders = data.orders || []
        }
      } catch (error) {
        console.error('Error loading orders:', error)
      } finally {
        this.loading = false
      }
    },

    async updateOrderStatus (order) {
      try {
        const token = localStorage.getItem('auth_token')
        const response = await fetch(`https://api.quali-d.com/restaurant-mode/orders/${order.id}/status`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            order_status: order.order_status
          })
        })

        const data = await response.json()
        if (data.success) {
          alert('Order status updated successfully!')
        } else {
          alert('Failed to update order status: ' + (data.message || 'Unknown error'))
          this.loadOrders() // Reload to revert
        }
      } catch (error) {
        console.error('Error updating order status:', error)
        alert('Failed to update order status')
        this.loadOrders() // Reload to revert
      }
    },

    parseItems (itemsJson) {
      try {
        return JSON.parse(itemsJson)
      } catch {
        return []
      }
    },

    getStatusClass (status) {
      const classes = {
        pending: 'status-pending',
        confirmed: 'status-confirmed',
        preparing: 'status-preparing',
        ready: 'status-ready',
        out_for_delivery: 'status-delivering',
        delivered: 'status-delivered',
        cancelled: 'status-cancelled'
      }
      return classes[status] || ''
    },

    getStatusLabel (status) {
      const labels = {
        pending: '⏳ Pending',
        confirmed: '✅ Confirmed',
        preparing: '👨‍🍳 Preparing',
        ready: '✅ Ready',
        out_for_delivery: '🚚 Delivering',
        delivered: '✅ Delivered',
        cancelled: '❌ Cancelled'
      }
      return labels[status] || status
    },

    formatDateTime (dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.restaurant-orders-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 280px;
}

.page-content {
  flex: 1;
  padding: 32px;
}

.restaurant-orders-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* Page Header */
.page-header {
  margin-bottom: 24px;
}

.header-content {
  background: var(--bg-primary);
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--border-primary);
}

.page-title {
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-icon {
  color: #3b82f6;
}

.page-subtitle {
  margin: 0;
  font-size: 15px;
  color: var(--text-secondary);
}

/* Filters */
.filters-section {
  background: var(--bg-primary);
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--border-primary);
}

.filter-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.filter-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border: 2px solid var(--border-primary);
  background: var(--bg-primary);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  color: var(--text-secondary);
}

.filter-btn:hover {
  border-color: #3b82f6;
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
}

.filter-btn.active {
  background: #3b82f6;
  border-color: #3b82f6;
  color: white;
}

.filter-icon {
  font-size: 16px;
}

/* Loading & Empty States */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: var(--text-secondary);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--border-primary);
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-icon {
  color: var(--text-tertiary);
  margin-bottom: 16px;
}

.empty-state h3 {
  margin: 0 0 8px 0;
  font-size: 20px;
  color: var(--text-primary);
}

.empty-state p {
  margin: 0;
  font-size: 14px;
}

/* Orders List */
.orders-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.order-card {
  background: var(--bg-primary);
  border: 2px solid var(--border-primary);
  border-radius: 12px;
  padding: 24px;
  transition: all 0.3s;
}

.order-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid var(--border-primary);
}

.order-number {
  margin: 0 0 4px 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
}

.order-date {
  font-size: 13px;
  color: var(--text-secondary);
}

.order-status {
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 700;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
}

.order-status.status-pending {
  background: #fef3c7;
  color: #d97706;
}

.order-status.status-confirmed,
.order-status.status-ready,
.order-status.status-delivered {
  background: #d1fae5;
  color: #059669;
}

.order-status.status-preparing {
  background: #dbeafe;
  color: #2563eb;
}

.order-status.status-delivering {
  background: #e0e7ff;
  color: #4f46e5;
}

.order-status.status-cancelled {
  background: #fee2e2;
  color: #dc2626;
}

/* Order Details */
.order-details {
  margin-bottom: 20px;
}

.detail-row {
  display: flex;
  align-items: baseline;
  gap: 12px;
  padding: 8px 0;
}

.detail-label {
  font-weight: 600;
  color: var(--text-secondary);
  font-size: 14px;
  min-width: 100px;
}

.detail-value {
  color: var(--text-primary);
  font-size: 14px;
}

.phone-link {
  color: #3b82f6;
  text-decoration: none;
}

.phone-link:hover {
  text-decoration: underline;
}

.total-amount {
  font-weight: 700;
  font-size: 16px;
  color: #3b82f6;
}

/* Order Items */
.order-items {
  margin-bottom: 20px;
  padding: 16px;
  background: var(--bg-tertiary);
  border-radius: 8px;
}

.items-title {
  margin: 0 0 12px 0;
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
}

.items-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.item-entry {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  font-size: 14px;
  color: var(--text-secondary);
}

.item-name {
  flex: 1;
  color: var(--text-primary);
}

.item-quantity {
  font-weight: 600;
}

.item-price {
  font-weight: 600;
  color: var(--text-primary);
}

/* Order Actions */
.order-actions {
  display: flex;
  justify-content: flex-end;
}

.status-select {
  padding: 10px 16px;
  border: 2px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: all 0.3s;
}

.status-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.status-select.status-pending {
  border-color: #d97706;
  color: #d97706;
}

.status-select.status-confirmed,
.status-select.status-ready,
.status-select.status-delivered {
  border-color: #059669;
  color: #059669;
}

.status-select.status-preparing {
  border-color: #2563eb;
  color: #2563eb;
}

.status-select.status-delivering {
  border-color: #4f46e5;
  color: #4f46e5;
}

.status-select.status-cancelled {
  border-color: #dc2626;
  color: #dc2626;
}

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 70px;
  }

  .page-content {
    padding: 16px;
  }

  .filter-buttons {
    gap: 8px;
  }

  .filter-btn {
    padding: 8px 12px;
    font-size: 13px;
  }

  .order-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .detail-row {
    flex-direction: column;
    gap: 4px;
  }

  .detail-label {
    min-width: auto;
  }
}
</style>
